import { useCallback, useEffect, useState } from "react";
import UserView from "./user-view";
import { IRecords, ViewType } from "../../../types";
import GenerateStrategy from "./generate-strategy";
import { TabContainer, Tab } from "./../../tab";
import TrendingOptions from "./trending-options";
import OptionChain from "./option-chain";
import strategyIcon from "./../../../images/idea.svg";
import chainIcon from "./../../../images/chain.png";
import trendingIcon from "./../../../images/trending.png";
import tradeIcon from "./../../../images/trade.png";
import positionsIcon from "./../../../images/positions.png";
import WatchlistTab from "../virtual-trading/watchlist-tab";
import PositionsTab from "../virtual-trading/positions-tab";
import {
  checkValidDate,
  clearApiTimeout,
  getData,
  isMarketActiveNow,
  isValidExpiry,
  storeData,
} from "../../../utils";
import { API } from "../../../services/constants";
import { httpGet } from "../../../services/httpGet";
import { EXPIRIES } from "./constants";
//import { getWatchList } from "../../../services/watchList";

interface INiftyProps {
  lotSize: number;
  shortMargin: number;
  marginBenefit: number;
  dataUrl: string;
  indexName: string;
  maxStrikes: number;
  isIndex?: boolean;
}

const Nifty = (props: INiftyProps) => {
  let [records, setRecords] = useState<IRecords>();
  const [indices, setIndices] = useState([]);
  const expiries = EXPIRIES[props.indexName].filter(val => isValidExpiry(val));
  let [view, setView] = useState({
    viewType: ViewType.Up,
    expiryDate: expiries[0],
    from: 0,
    to: 0,
    allowedBreakeven: 0,
  });

  const baseUrl = "https://optionstrategies.in/home/";
  // const baseUrl = 'https://localhost:44306/home/'
  const urlPathToEncode = encodeURIComponent(`option-chain-v3?type=Indices&symbol=${props.indexName.replace(" ", "").toUpperCase()}&expiry=${view.expiryDate}`);
  const url = `${baseUrl}getalldata?url=${urlPathToEncode}`;
  const allIndices = `${baseUrl}${API.ALL_INDICES}`;


  const fetchAllIndices = useCallback(async () => {
    const res = await httpGet(allIndices);
    if(res) {
      setIndices(res.data)
    } else {
      clearApiTimeout(allIndices);
      (window as any)[allIndices] = setTimeout(fetchAllIndices, 5000);
    }
    return res;
  }, [allIndices])


  const fetchData = useCallback(async () => {
    const isMarketActive = isMarketActiveNow();
    const dataInCache = getData(url + "cachedData");

    if (
      isMarketActive ||
      !dataInCache.time ||
      !checkValidDate(new Date(dataInCache.time))
    ) {
      const res = await httpGet(url);
      if (!res) {
        clearApiTimeout("niftyTimeout");
        (window as any).niftyTimeout = setTimeout(fetchData, 5000);
      } else {
        if (res.records) {
          setRecords((rec) =>
            rec
              ? {
                  ...rec,
                  records: res.records,
                }
              : res.records
          );
          storeData(url + "cachedData", {
            time: new Date().getTime(),
            data: {
              ...res.records,
            },
          });
        } else {
          //If data fetching failed, fetch data again after 5 seconds
          clearApiTimeout(url);
          (window as any)[url] = setTimeout(fetchData, 5000);
        }
      }
    } else {
      setRecords(dataInCache.data);
    }
  }, [url]);

  const handleViewSelected = (selectedView: any) => {
    view = selectedView;
    setView({ ...view });
    fetchData();
  };

  const handleRefresh = () => {
    setRecords(undefined);
    fetchData();
  };

  const fetchDataForExpiry = (expiry: string) => {
    view.expiryDate = expiry;
    setView({ ...view });
    setRecords(undefined);
    fetchData();
  };

  const fetchAll = useCallback(() => {
    fetchData();
    fetchAllIndices();
  }, [fetchAllIndices, fetchData]);


  useEffect(() => {
    fetchAll();
    let interval: any = null;
    if (isMarketActiveNow()) interval = setInterval(fetchAll, 20000);

    return () => {
      interval && clearInterval(interval);
      clearApiTimeout(url);
      clearApiTimeout(allIndices);
    };
  }, [allIndices, fetchAll, fetchData, url]);

  return (
    <div className="e-settings-layout">
      {records ? (
        <TabContainer style={{ marginBottom: "5px" }}>
          <Tab name="Strategies" icon={strategyIcon}>
            <UserView
              onRefresh={handleRefresh}
              indexName={props.indexName}
              data={{ ...records, indices }}
              onViewSelected={handleViewSelected}
              expiryDates={records.expiryDates ?? []}
            ></UserView>
            {view.expiryDate && (
              <GenerateStrategy
                {...props}
                {...view}
                data={records}
              ></GenerateStrategy>
            )}
          </Tab>
          <Tab name="Watchlist" icon={tradeIcon}>
            <WatchlistTab
              dataSource={records.data}
              expiries={records.expiryDates}
              UnderlyingName={props.indexName}
              lotSize={props.lotSize}
              fetchData={fetchDataForExpiry}
              expiry={view.expiryDate}
              selectedExpiry={fetchDataForExpiry}
            />
          </Tab>
          <Tab name="Positions" icon={positionsIcon}>
            <PositionsTab
              indexName={props.indexName}
              data={{ [props.indexName]: records }}
              fetchData={fetchDataForExpiry}
              expiry={view.expiryDate}
              selectedExpiry={fetchDataForExpiry}
              expiries={records.expiryDates}
            />
          </Tab>
          <Tab name="OI Data" icon={chainIcon}>
            <OptionChain
              expiry={view.expiryDate}
              selectedExpiry={fetchDataForExpiry}
              data={records}
            />
          </Tab>
          <Tab name="Trending" icon={trendingIcon}>
            <TrendingOptions
              dataSource={records.data}
              expiries={records.expiryDates}
              expiry={view.expiryDate}
              selectedExpiry={fetchDataForExpiry}
            ></TrendingOptions>
          </Tab>
        </TabContainer>
      ) : (
        <div style={{ textAlign: "center" }}>
          <p>
            Please wait for the data to load. It might take a minute or two due
            to restrictions on the number of data request imposed by our data
            vendor.
          </p>
          <p>
            If data not received, this app will try to get the data
            automatically after every 5 seconds.
          </p>
          <progress></progress>
          <p>
            If the data is not available even after few minutes please inform us
            at support@optionstrategies.in or in our telegram channel
            https://t.me/optionstrategybuilder
          </p>
        </div>
      )}
    </div>
  );
};

export default Nifty;
